import { CryptoAsset, formatCurrency } from "@/lib/crypto-data";
import { TrendingUp, TrendingDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";

interface CryptoCardProps {
  crypto: CryptoAsset;
  onBuy: (crypto: CryptoAsset) => void;
}

const CryptoCard = ({ crypto, onBuy }: CryptoCardProps) => {
  const isPositive = crypto.change24h >= 0;
  const { t } = useTranslation();

  return (
    <div className="group relative overflow-hidden rounded-xl glass shadow-card p-5 transition-all duration-300 hover:scale-[1.02] hover:shadow-glow animate-fade-in">
      <div 
        className="absolute inset-0 opacity-10 blur-3xl transition-opacity group-hover:opacity-20"
        style={{ background: crypto.color }}
      />
      
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            {crypto.image ? (
              <img src={crypto.image} alt={crypto.name} className="w-10 h-10 rounded-full" loading="lazy" />
            ) : (
              <div className="flex items-center justify-center w-10 h-10 rounded-full text-xl font-bold" style={{ background: crypto.color, color: 'hsl(var(--background))' }}>
                {crypto.icon}
              </div>
            )}
            <div>
              <h3 className="font-bold text-base text-foreground">{crypto.symbol}</h3>
              <p className="text-xs text-muted-foreground">{crypto.nameAr}</p>
            </div>
          </div>
          
          <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
            isPositive ? 'bg-crypto-green/20 text-crypto-green' : 'bg-crypto-red/20 text-crypto-red'
          }`}>
            {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {isPositive ? '+' : ''}{crypto.change24h.toFixed(2)}%
          </div>
        </div>

        <div className="mb-3">
          <p className="text-2xl font-bold text-foreground">{formatCurrency(crypto.price)}</p>
        </div>

        <Button 
          onClick={() => onBuy(crypto)}
          className="w-full gradient-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity"
          size="sm"
        >
          {t('dashboard.buy')} {crypto.symbol}
        </Button>
      </div>
    </div>
  );
};

export default CryptoCard;
